<script>
import lazyLoadComponent from '@router/lazyload-component'

import { genderFilter, orderStatusMap, orderStatusFilter, orderPaymentMethodMap, orderPaymentMethodFilter, orderSourceMap, orderSourceFilter } from '@utils/filter-value-to-text'
import formatDate, { formatRequestData } from '@utils/format-date'
import { debounce } from 'lodash'

import pageMixin from '@src/mixins/pagination'

export default {
  page: {
    title: '訂單查詢',
    meta: [{ name: 'description', content: 'Order List' }],
  },
  components: {
    BaseStateLabel: () => lazyLoadComponent(import('@components/BaseStateLabel')),
    PrepaidOrderDetail: () => import('@components/Orders/PrepaidOrderDetail'),
  },
  filters: {
    genderFilter,
    orderStatusFilter,
    orderPaymentMethodFilter,
    orderSourceFilter,
    formatDate,
  },
  mixins: [pageMixin],
  data() {
    return {
      drawerVisible: false,
      drawerOrderData: '',
      filterData: {
        search: '',
        status: '',
        source: '',
        startDate: '',
        endDate: '',
        paymentMethod: 'All',
        order_by: 'created_at',
        order: 'desc',
      },
      statusList: Object.freeze(orderStatusMap),
      sourceList: Object.freeze(orderSourceMap),
      paymentMethodList: Object.freeze(this.reduceMethod({ ...orderPaymentMethodMap })),
      pickerOptions: {
        shortcuts: [
          {
            text: '最近一週',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
              picker.$emit('pick', [start, end])
            },
          },
          {
            text: '最近一個月',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
              picker.$emit('pick', [start, end])
            },
          },
          {
            text: '最近三個月',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
              picker.$emit('pick', [start, end])
            },
          },
        ],
      },
      loadedOrders: [],
      prepaidOrders: [],
      tryingToGetOrder: false,
      tryingToExport: false,
      isFilterChange: false,
      componentKey: 0,
      appStyle: process.env.VUE_APP_BUILD_MODE === 'admin-app-build',
      productList: [],
    }
  },
  computed: {
    statusListFilter() {
      let obj = { ...orderStatusMap }
      delete obj['3']
      delete obj['4']
      delete obj['5']
      delete obj['6']
      return obj
    },
    filterDateRange: {
      get() {
        return [this.filterData.startDate, this.filterData.endDate]
      },
      set(value) {
        const [start, end] = value || ['', '']
        this.filterData.startDate = start
        this.filterData.endDate = end
      },
    },
    fetchDataParams() {
      const routeQuery = this.$route.query
      const queryData = Object.assign(routeQuery, this.pageQuery)
      const { startDate, endDate, dateType, ...filterDataWithoutDate } = this.filterData
      const searchWithDate = startDate && endDate
      const searchParams = searchWithDate
        ? Object.assign(this.filterData, {
            startDate: formatRequestData(startDate),
            endDate: formatRequestData(endDate),
          })
        : filterDataWithoutDate
      return Object.assign(searchParams, queryData)
    },
  },
  watch: {
    currentPage: {
      handler: 'tryToFetchList',
      immediate: true,
    },
    filterData: {
      handler() {
        this.isFilterChange = true
      },
      deep: true,
    },
    drawerVisible(value) {
      if (!value) {
        if (this.$route.query.type === 'prepaidOrder') {
          this.$router.push({
            name: 'orders',
            params: {
              activeTab: '1',
            },
            query: {},
          })
        }
      }
    },
  },
  created() {
    this.tryToSearchProduct()
    if (this.$route.query.type === 'prepaidOrder') {
      this.tryToGetOrder(this.$route.query.id)
    }
    if (this.$route.params.type === 'prepaidOrder') {
      this.tryToGetOrder(this.$route.params.id)
    }
  },
  methods: {
    async tryToSearchProduct() {
      const { items } = await this.$store.dispatch('product/fetchList', { limit: 10000, page: 1, paginate: 0 })
      this.productList = items
    },
    reduceMethod(list) {
      delete list.Prepaid
      if (!this.$hasDeposit()) {
        delete list.Deposit
      }
      if (!this.$cashFlowSettingPass()) {
        delete list.Vacc
        delete list.Credit
      }
      return { ...list }
    },
    async handleExport() {
      if (this.tryingToExport) return
      this.tryingToExport = true
      const { fileName, csvData } = await this.$store.dispatch('prepaid/simpleExport', this.fetchDataParams)
      const csvFormatData = csvData.map((row) => Object.values(row).join(',')).join('\n')

      const blob = new Blob(['\uFEFF', csvFormatData], {
        type: 'text/csv;charset=utf-8;',
      })

      const url = window.URL.createObjectURL(blob)

      const tempLink = document.createElement('a')
      tempLink.download = fileName
      tempLink.href = url
      tempLink.click()
      window.URL.revokeObjectURL(url)
      this.tryingToExport = false
    },
    tableRowClassName({ row }) {
      const rowStatus = row.status
      const usedStatus = row.groupSize / row.usedSize
      if (rowStatus === 0) return 'order--canceled'
      if (usedStatus === 1) return 'order--used'
    },
    async tryToGetOrder(orderId) {
      try {
        this.tryingToGetOrder = true
        this.drawerOrderData = await this.$store.dispatch('prepaid/fetchOrderSingle', orderId)
        this.drawerVisible = true
      } catch {
      } finally {
        this.tryingToGetOrder = false
      }
    },
    /**
     * @description 因為使用router push 打開的畫面，沒有petSizeTitle所以要自己塞
     */
    async tryToFetchListFilter() {
      // 當篩選前不在第一頁的時候，跳回第一頁
      if (this.currentPage !== 1) {
        this.currentPage = 1
        return
      }
      const routeQuery = this.$route.query
      const queryData = Object.assign(routeQuery, this.pageQuery)

      const { startDate, endDate } = this.filterData

      const { items, total } = await this.$store.dispatch(
        'prepaid/fetchOrders',
        Object.assign(
          this.filterData,
          {
            startDate: formatRequestData(startDate),
            endDate: formatRequestData(endDate),
          },
          queryData
        )
      )
      this.loadedOrders = items
      await this.getPrepaidInfo()
      this.$nextTick(() => {
        if (this.$refs.pagination) {
          this.$refs.pagination.updateTotal(total)
        }
      })
      this.isFilterChange = false
    },
    currentPageLimit() {
      const remainder = this.$refs.pagination.total % this.$refs.pagination.pageSize
      const qotient = this.$refs.pagination.total / this.$refs.pagination.pageSize
      const totalPage = remainder ? parseInt(qotient) + 1 : parseInt(qotient)
      if (this.currentPage > totalPage) {
        this.currentPage = totalPage
      }
    },
    async tryToFetchList() {
      const routeQuery = this.$route.query
      const queryData = Object.assign(routeQuery, this.pageQuery)

      const { startDate, endDate } = this.filterData

      const { items, total } = await this.$store.dispatch(
        'prepaid/fetchOrders',
        Object.assign(
          this.filterData,
          {
            startDate: formatRequestData(startDate),
            endDate: formatRequestData(endDate),
          },
          queryData
        )
      )
      this.loadedOrders = items
      this.$nextTick(() => {
        this.$refs.pagination && this.$refs.pagination.updateTotal(total)
        this.currentPageLimit()
      })
    },
    clearSearchQuery() {
      this.$router.push(this.$route.path)
    },
    /**
     * @description 點擊該欄位之後要做的事
     * @param {Object} row
     */
    onCellClick(row) {
      this.tryToGetOrder(row.id)
    },
    sortChange({ column, prop, order }) {
      if (order) {
        this.filterData.order_by = prop
        this.filterData.order = order === 'ascending' ? 'asc' : 'desc'
        this.currentPage = 1
      } else {
        this.filterData.order_by = 'created_at'
        this.filterData.order = 'desc'
      }
      this.tryToFetchList()
    },
    tryToSearchByInput: debounce(function() {
      this.tryToFetchList()
    }, 300),
    async getPrepaidInfo() {
      this.tryingToGetOrder = true
      this.prepaidOrders = []
      for (const item of this.loadedOrders) {
        const data = await this.$store.dispatch('prepaid/fetchOrderSingle', item.id)
        this.prepaidOrders.push(data)
      }
      this.tryingToGetOrder = false
    },
    prepaidUsedTimes(data) {
      return `${data.name}: ${data.maxTimes - data.used} / ${data.maxTimes}`
    },
    getPaymentMethod(arr) {
      let result = 'undefined'
      if (Array.isArray(arr) && arr.length === 1) {
        result = arr[0]
      }
      return result
    },
    getInstallmentUnpayCount(orderArr) {
      let result = 0
      if (Array.isArray(orderArr)) {
        let UnpayOrderArr = orderArr.filter((order) => {
          return order.paymentStatus !== 'PAID'
        })
        result = UnpayOrderArr.length
      }
      return result
    },
  },
}
</script>

<template>
  <div>
    <div :class="$style.btnTop" class="btn-top-space-between">
      <div class="side search">
        <el-input v-model.trim.lazy="filterData.search" clearable placeholder="輸入使用人姓名、手機號碼或訂單編號" @input="tryToSearchByInput">
          <BaseIcon slot="suffix" name="search" class="el-input__icon" />
        </el-input>
      </div>
      <div class="side btn ml-md-3">
        <el-button class="is-round" plain @click="handleExport"> <BaseIcon name="file-export" /> 訂單匯出 </el-button>
        <el-button class="is-round" type="primary" @click="$router.push({ name: 'prepaid' })"> <BaseIcon name="plus" /> 代客購買{{ `${$t('prepaid.BASE')}` }} </el-button>
      </div>
    </div>
    <BaseTableSearch v-slot="{ fields }" class="isForTab" hide-action :default-value.sync="filterData" :class="$style.filter" @reset="tryToFetchList" @tryToFetchList="tryToFetchListFilter">
      <el-form-item prop="status" style="width: 19%">
        <el-select v-model="fields.status" placeholder="選擇訂單狀態" @change="tryToFetchListFilter">
          <el-option value>所有訂單狀態</el-option>
          <el-option v-for="(status, statusValue) in statusListFilter" :key="statusValue" :label="status.text" :value="statusValue"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item prop="paymentMethod" style="width: 19%">
        <el-select v-model="fields.paymentMethod" placeholder="選擇付款方式" @change="tryToFetchListFilter">
          <el-option label="所有付款方式" value="All"></el-option>
          <template v-for="(label, value) in paymentMethodList">
            <el-option v-if="value !== 'Prepaid'" :key="value" :label="label" :value="value"></el-option>
          </template>
        </el-select>
      </el-form-item>

      <el-form-item style="width: 41%">
        <el-date-picker
          v-model="filterDateRange"
          type="daterange"
          align="right"
          unlink-panels
          range-separator="至"
          start-placeholder="起始日期"
          end-placeholder="結束日期"
          :picker-options="pickerOptions"
          @change="tryToFetchListFilter"
        ></el-date-picker>
      </el-form-item>
    </BaseTableSearch>
    <div class="isStylePageContent">
      <div class="el-table-wrapper">
        <el-table
          v-loading.fullscreen.lock="tryingToGetOrder"
          element-loading-text="取得訂單資訊中"
          :data="loadedOrders"
          :default-sort="{ prop: 'created', order: 'descending' }"
          :row-class-name="tableRowClassName"
          @cell-click="onCellClick"
          @sort-change="sortChange"
        >
          <el-table-column v-slot="{ row }" label="訂單編號" prop="created_at" min-width="180" sortable>
            <div class="Jost fs-sm fw-medium">{{ `#${row.orderNo}` }}</div>
            <div class="fs-2sm text-secondary-light">{{ row.created | formatDate('dateTime') }}</div>
          </el-table-column>
          <el-table-column v-slot="{ row }" label="付款方式與狀態" prop="status" min-width="160" sortable="custom">
            <BaseStateLabel :state="row.status | orderStatusFilter('color')" text>{{ row.status | orderStatusFilter('text') }}</BaseStateLabel>
            <span v-if="row.paymentMethod === 'Installment' && getInstallmentUnpayCount(row.installments) > 0" style="color: #F25555">({{ getInstallmentUnpayCount(row.installments) }}期未付款)</span>
            <br />
            <BaseStateLabel state="secondary" text class="fs-2sm">{{ row.source | orderSourceFilter }}</BaseStateLabel>
            <template>
              <span v-if="row.paymentMethod !== 'Installment' && row.paymentMethod !== 'Installment'" class="fs-2sm ml-3">{{ row.paymentMethod | orderPaymentMethodFilter }}</span>
              <span v-else class="fs-2sm ml-3">{{ row.installments.length }}期付款</span>
            </template>
            <br />
            <span v-if="row.invoiceNumber">{{ row.invoiceNumber }}</span>
          </el-table-column>

          <el-table-column v-slot="{ row }" min-width="150" label="客戶資訊">
            <div>{{ row.fullName }} {{ row.gender | genderFilter }}</div>
            <div class="fs-2sm  text-secondary-light">{{ `${row.phoneCode} ${row.phoneNumber}` }}</div>
          </el-table-column>
          <el-table-column v-slot="{ row }" min-width="150" label="分店">
            <div>{{ row.storeTitles.join(' | ') }}</div>
          </el-table-column>
          <el-table-column v-slot="{ row }" :label="`${$t('prepaid.BASE')}方案名稱`" min-width="250">
            <div>
              <b>{{ row.prepaidTitle }}</b> <br />
              <span class="text-muted">{{ row.categoryTitle }} | {{ row.sizeTitle }}</span>
              <br />
              <span class="text-muted">可抵用次數：{{ `${row.times - row.used} / ${row.times}` }}</span>
              <span v-for="(prepaid, index) in row.prices" :key="`${prepaid.id}-${index}`" class="text-muted" style="display: block;">{{ prepaidUsedTimes(prepaid) }}</span>
              <span v-if="row.status === 1" class="text-muted">到期：{{ row.expiredDate ? row.expiredDate : '' | formatDate }}{{ row.expiredDate ? '' : '無使用期限' }}</span>
            </div>
            <div>{{ row.date | formatDate }} {{ row.time }}</div>
          </el-table-column>
          <el-table-column v-slot="{ row }" min-width="130" label="金額" prop="total">
            <span class="Jost">${{ row.total }}</span>
          </el-table-column>
          <el-table-column min-width="40">
            <el-tooltip content="查看訂單詳情" placement="top">
              <el-button type="text">
                <BaseIcon name="ellipsis-v" />
              </el-button>
            </el-tooltip>
          </el-table-column>
        </el-table>
      </div>
      <BasePagination ref="pagination" :current-page.sync="currentPage" />
    </div>
    <el-drawer
      ref="drawer"
      class="only-close"
      :class="{ appStyle: appStyle }"
      custom-class="order-drawer"
      append-to-body
      :modal-append-to-body="false"
      :wrapper-closable="false"
      :visible.sync="drawerVisible"
      :size="$root.isSmallDevice ? '100%' : '90%'"
      :destroy-on-close="true"
      @closed="componentKey++"
    >
      <PrepaidOrderDetail :default-value.sync="drawerOrderData" order-type="prepaid" @update="tryToFetchList" />
    </el-drawer>
  </div>
</template>

<style lang="scss" module>
:global {
  :local(.filter) {
    & > div {
      flex-wrap: wrap;

      .el-form-item {
        @include max-lg {
          width: 100% !important;
        }
      }

      & + button {
        align-self: baseline;
      }
    }
  }
}

.btnTop {
  padding: 0.5rem 0;

  @include max-sm {
    display: flex;
    flex-direction: column;
  }

  :global {
    .side {
      @include max-sm {
        display: flex;
        // flex-direction: column;
      }

      &:first-child {
        @include max-sm {
          margin-bottom: 0.5rem;

          .el-button {
            flex: 1 0 0;
            width: 50%;
          }
        }
      }

      .el-input {
        @include max-sm {
          margin-left: 0 !important;
          width: 100%;
        }
      }
    }
  }
}
</style>
